import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { AppDispatch, RootState } from "../../store/store";
import { useAuth } from "../../hooks/useAuth";
import {
  fetchBookingsByCustomer,
  cancelBooking,
} from "../../store/slice/booking/bookingThunks";
import {
  fetchPaymentsForBooking,
  initializePayment,
  verifyPayment,
} from "../../store/slice/payment/paymentThunks";
import { submitReview } from "../../store/slice/review/reviewThunks";
import LoadMoreButton from "../../components/LoadMoreButton";

const statusColor: Record<string, string> = {
  PENDING: "text-yellow-600",
  CONFIRMED: "text-green-600",
  DECLINED: "text-red-600",
  COMPLETED: "text-blue-600",
  CANCELLED: "text-gray-500",
};

const paymentStatusColor: Record<string, string> = {
  INITIATED: "text-yellow-600",
  SUCCESS: "text-green-600",
  FAILED: "text-red-600",
};

const CustomerDashboard: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useAuth();
  const { bookings, pageInfo, loading, error } = useSelector(
    (state: RootState) => state.booking
  );
  const { paymentsByBooking, loading: paymentLoading } = useSelector(
    (state: RootState) => state.payment
  );
  const { error: reviewError } = useSelector((state: RootState) => state.review);

  const [payerEmail, setPayerEmail] = useState(user?.email || "");

  // The profile often loads after first render; fill the email once it arrives
  useEffect(() => {
    if (user?.email) {
      setPayerEmail((current) => current || user.email);
    }
  }, [user?.email]);

  const [reviewFormOpenFor, setReviewFormOpenFor] = useState<number | null>(
    null
  );
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");
  const [reviewedBookingIds, setReviewedBookingIds] = useState<Set<number>>(
    new Set()
  );
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  useEffect(() => {
    if (user?.id) {
      dispatch(fetchBookingsByCustomer(user.id));
    }
  }, [dispatch, user?.id]);

  useEffect(() => {
    bookings
      .filter((b) => b.status === "COMPLETED" && !(b.id in paymentsByBooking))
      .forEach((b) => dispatch(fetchPaymentsForBooking(b.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookings]);

  const handlePayNow = async (bookingId: number) => {
    if (!payerEmail) {
      alert("Enter your email before paying.");
      return;
    }
    const result = await dispatch(initializePayment(bookingId, payerEmail));
    if (result?.authorizationUrl) {
      // Same tab: Paystack redirects back to /payments/callback when done
      window.location.assign(result.authorizationUrl);
    }
  };

  const handleRefreshPaymentStatus = (bookingId: number, reference: string) => {
    dispatch(verifyPayment(reference, bookingId));
  };

  const handleOpenReviewForm = (bookingId: number) => {
    setReviewFormOpenFor(bookingId);
    setReviewRating(5);
    setReviewComment("");
  };

  const handleSubmitReview = async (bookingId: number) => {
    setReviewSubmitting(true);
    const result = await dispatch(
      submitReview({
        bookingId,
        customerId: Number(user?.id),
        rating: reviewRating,
        comment: reviewComment || undefined,
      })
    );
    setReviewSubmitting(false);

    if (result) {
      setReviewedBookingIds((prev) => new Set(prev).add(bookingId));
      setReviewFormOpenFor(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      <h1 className="text-2xl font-bold text-gray-900">Customer Dashboard</h1>

      {error && <p className="text-red-600">{error}</p>}

      {/* Email used for payments */}
      <div>
        <label className="block mb-1 text-sm font-medium text-gray-900">
          Your Email (used for payment)
        </label>
        <input
          type="email"
          required
          value={payerEmail}
          onChange={(e) => setPayerEmail(e.target.value)}
          className="w-full p-2.5 text-sm rounded-lg border border-gray-300"
        />
      </div>

      {/* New booking - now made from a provider's profile page, where the
          real handyman ID/service/price are already known, instead of
          typing a raw ID here. */}
      <div className="border border-gray-200 rounded-lg p-4 text-center">
        <p className="text-gray-700 mb-3">
          Ready to book someone? Find a handyman first.
        </p>
        <Link
          to="/search"
          className="inline-block py-2.5 px-5 text-sm font-medium text-white rounded-lg bg-green-600 hover:bg-green-700"
        >
          Find a Handyman
        </Link>
      </div>

      {/* Bookings list */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-3">My Bookings</h2>
        {bookings.length === 0 ? (
          <p className="text-gray-500 text-sm">No bookings loaded yet.</p>
        ) : (
          <ul className="space-y-2">
            {bookings.map((b) => {
              const payments = paymentsByBooking[b.id] || [];
              const latestPayment = payments[payments.length - 1];

              return (
                <li
                  key={b.id}
                  className="border border-gray-200 rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium text-gray-900">
                        #{b.id} — {b.serviceType} at {b.location}
                      </p>
                      <p className="text-sm text-gray-500">
                        {new Date(b.bookingDate).toLocaleString()} · ₦{b.amount}
                      </p>
                    </div>
                    <span className={`font-semibold ${statusColor[b.status]}`}>
                      {b.status}
                    </span>
                  </div>

                  {(b.status === "PENDING" || b.status === "CONFIRMED") && (
                    <button
                      disabled={loading}
                      onClick={() => dispatch(cancelBooking(b.id))}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-gray-500 hover:bg-gray-600 disabled:bg-gray-400"
                    >
                      Cancel Booking
                    </button>
                  )}

                  {b.status === "COMPLETED" &&
                    (!latestPayment || latestPayment.status === "FAILED") && (
                    <button
                      disabled={paymentLoading}
                      onClick={() => handlePayNow(b.id)}
                      className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                    >
                      {paymentLoading
                        ? "Starting payment..."
                        : latestPayment
                        ? "Try Payment Again"
                        : "Pay Now"}
                    </button>
                  )}

                  {latestPayment && (
                    <div className="flex items-center justify-between text-sm">
                      <span>
                        Payment:{" "}
                        <span
                          className={`font-semibold ${
                            paymentStatusColor[latestPayment.status]
                          }`}
                        >
                          {latestPayment.status}
                        </span>
                      </span>
                      {latestPayment.status === "INITIATED" && (
                        <button
                          disabled={paymentLoading}
                          onClick={() =>
                            handleRefreshPaymentStatus(
                              b.id,
                              latestPayment.paystackReference
                            )
                          }
                          className="py-1 px-2 text-xs font-medium text-white rounded-lg bg-gray-700 hover:bg-gray-800 disabled:bg-gray-400"
                        >
                          Refresh Status
                        </button>
                      )}
                    </div>
                  )}

                  {b.status === "COMPLETED" &&
                    !reviewedBookingIds.has(b.id) &&
                    (reviewFormOpenFor === b.id ? (
                      <div className="border-t border-gray-100 pt-2 space-y-2">
                        <div>
                          <label className="block mb-1 text-xs font-medium text-gray-700">
                            Rating
                          </label>
                          <select
                            value={reviewRating}
                            onChange={(e) =>
                              setReviewRating(Number(e.target.value))
                            }
                            className="p-1.5 text-sm rounded border border-gray-300"
                          >
                            {[5, 4, 3, 2, 1].map((n) => (
                              <option key={n} value={n}>
                                {"⭐".repeat(n)} ({n})
                              </option>
                            ))}
                          </select>
                        </div>
                        <textarea
                          value={reviewComment}
                          onChange={(e) => setReviewComment(e.target.value)}
                          placeholder="Optional comment"
                          className="w-full p-2 text-sm rounded border border-gray-300"
                        />
                        {reviewError && (
                          <p className="text-red-600 text-xs">{reviewError}</p>
                        )}
                        <div className="flex gap-2">
                          <button
                            disabled={reviewSubmitting}
                            onClick={() => handleSubmitReview(b.id)}
                            className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-green-600 hover:bg-green-700 disabled:bg-gray-400"
                          >
                            {reviewSubmitting ? "Submitting..." : "Submit Review"}
                          </button>
                          <button
                            onClick={() => setReviewFormOpenFor(null)}
                            className="py-1.5 px-3 text-sm font-medium text-gray-700 rounded-lg border border-gray-300 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleOpenReviewForm(b.id)}
                        className="py-1.5 px-3 text-sm font-medium text-white rounded-lg bg-yellow-600 hover:bg-yellow-700"
                      >
                        Leave a Review
                      </button>
                    ))}

                  {reviewedBookingIds.has(b.id) && (
                    <p className="text-sm text-green-600 font-medium">
                      Thanks for your review ✓
                    </p>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        <LoadMoreButton
          shown={bookings.length}
          total={pageInfo.totalElements}
          last={pageInfo.last}
          loading={loading}
          onClick={() =>
            user?.id && dispatch(fetchBookingsByCustomer(user.id, pageInfo.page + 1))
          }
        />
      </div>
    </div>
  );
};

export default CustomerDashboard;
